import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

const MONDAY_TOKEN = 'eyJhbGciOiJIUzI1NiJ9.eyJ0aWQiOjcxNjg3Mzg3NSwiYWFpIjoxMSwidWlkIjo1Njg0MDcyMiwiaWFkIjoiMjAyNi0xMC0yMlQxOTowMDo1MC43NTdaIiwicGVyIjoibWU6d3JpdGUiLCJhY3RpZCI6MjE2ODQ0ODAsInJnbiI6InVzZTEifQ.BZBy9pZvNYm61Jrx7WQm4d3rUEdssZSNE97ThLImBBE';
const BOARD_ID = '18410054007';

async function gql(query: string) {
  const res = await fetch('https://api.monday.com/v2', {
    method: 'POST',
    headers: {
      'Authorization': MONDAY_TOKEN,
      'Content-Type': 'application/json',
      'API-Version': '2024-01'
    },
    body: JSON.stringify({ query })
  });
  const json = await res.json() as any;
  if (json.errors) throw new Error(json.errors[0].message);
  return json.data;
}

export const findCustomerJob = createServerFn({ method: 'GET' })
  .validator(z.object({ customerName: z.string() }))
  .handler(async ({ data }) => {
    // Step 1: Fetch parent items (no subitems — fast)
    const mainData = await gql(`{
      boards(ids: ${BOARD_ID}) {
        items_page(limit: 500) {
          items {
            id
            name
            created_at
            column_values(ids: ["board_relation_mm2qcj5z","text_mm2xc4a","color_mm3jg3ae","board_relation_mm3c51pb","file_mm2taxm5","long_text_mm3kcdrq","date_mm3jmjv3","date_mm4ry0y","date_mm5310ax","text_mm4rzfd4","date_mm3jza4r"]) {
              id
              text
              value
              ... on BoardRelationValue {
                linked_items { id name }
              }
            }
          }
        }
      }
    }`);

    const items: any[] = mainData?.boards?.[0]?.items_page?.items ?? [];
    const searchTerm = data.customerName.toLowerCase().trim();

    // Step 2: Filter by QB Customer name OR item name
    const filtered = items.filter((item) => {
      const qbCol = item.column_values?.find((c: any) => c.id === 'board_relation_mm2qcj5z');
      const qbName: string = (qbCol?.linked_items?.[0]?.name ?? '').toLowerCase().trim();
      const itemName: string = (item.name ?? '').toLowerCase().trim();

      if (searchTerm.length < 2) return false;
      return (qbName.length > 0 && qbName.includes(searchTerm)) ||
             (itemName.length > 0 && itemName.includes(searchTerm));
    });

    // Step 3: Fetch subitems ONLY for matched jobs (keeps it fast)
    const subitemsMap: Record<string, any[]> = {};
    const assetsMap: Record<string, any[]> = {};
    const matchedIds = filtered.map((item) => item.id);

    if (matchedIds.length > 0) {
      const batchSize = 25;
      for (let i = 0; i < matchedIds.length; i += batchSize) {
        const batch = matchedIds.slice(i, i + batchSize);
        const subData = await gql(`{
          items(ids: [${batch.join(',')}]) {
            id
            assets(column_ids: ["file_mm2taxm5"]) { id name public_url }
            subitems {
              id
              name
              column_values(ids: ["date_mm2txpgk","date_mm2t70x2","date_mm2tz27x","date_mm2trt4p","date_mm2t10rj","date_mm2t9ges","date_mm3sena5","date_mm44qm17"]) {
                id
                text
              }
            }
          }
        }`);
        for (const item of (subData?.items ?? [])) {
          subitemsMap[item.id] = item.subitems ?? [];
          assetsMap[String(item.id)] = item.assets ?? [];
        }
      }
    }

    // Step 4: Sort by status priority (in-process > received > archive), then by received date
    const getStatusPriority = (status: string | null): number => {
      if (!status) return 2; // default to middle priority
      const s = status.toLowerCase();
      // Archive last
      if (s.includes('archive')) return 3;
      // Received second
      if (s === 'received') return 2;
      // Everything else is "in process" - first
      return 1;
    };

    filtered.sort((a, b) => {
      const statusA = a.column_values?.find((c: any) => c.id === 'color_mm3jg3ae')?.text ?? null;
      const statusB = b.column_values?.find((c: any) => c.id === 'color_mm3jg3ae')?.text ?? null;
      
      const prioA = getStatusPriority(statusA);
      const prioB = getStatusPriority(statusB);
      
      // First sort by priority
      if (prioA !== prioB) return prioA - prioB;
      
      // Within same priority, sort by received date (newest first)
      const rA = a.column_values?.find((c: any) => c.id === 'date_mm3jmjv3')?.text ?? '';
      const rB = b.column_values?.find((c: any) => c.id === 'date_mm3jmjv3')?.text ?? '';
      if (!rA && !rB) return 0;
      if (!rA) return 1;
      if (!rB) return -1;
      return new Date(rB).getTime() - new Date(rA).getTime();
    });

    // Step 5: Build response
    const jobs = filtered.map((item) => {
      const col = (id: string) => item.column_values?.find((c: any) => c.id === id);
      const qbCol = col('board_relation_mm2qcj5z');
      const colorCol = col('board_relation_mm3c51pb');
      // Photos come only from THIS item's own Po Upload column
      const files: Array<{ assetId?: string; name: string; url: string }> = [];
      for (const a of (assetsMap[String(item.id)] ?? []) as any[]) {
        if (a?.public_url) {
          files.push({ assetId: String(a.id), name: a.name ?? 'Photo', url: a.public_url });
        }
      }

      // Count completed racks per station
      const subs = subitemsMap[item.id] ?? [];
      const rackProgress = { prep: 0, wash: 0, spray: 0, qc: 0, total: subs.length };
      for (const sub of subs) {
        const g = (id: string) => sub.column_values?.find((c: any) => c.id === id)?.text;
        if (g('date_mm2txpgk') && g('date_mm2t70x2')) rackProgress.prep++;
        if (g('date_mm2tz27x') && g('date_mm2trt4p')) rackProgress.wash++;
        if (g('date_mm2t10rj') && g('date_mm2t9ges')) rackProgress.spray++;
        if (g('date_mm3sena5') && g('date_mm44qm17')) rackProgress.qc++;
      }

      return {
        id: item.id as string,
        name: item.name as string,
        lotpo: (col('text_mm2xc4a')?.text as string) ?? null,
        status: (col('color_mm3jg3ae')?.text as string) ?? null,
        jobNotes: (col('long_text_mm3kcdrq')?.text as string) ?? null,
        received: (col('date_mm3jmjv3')?.text as string) ?? null,
        due: (col('date_mm4ry0y')?.text as string) ?? null,
        invoiceDate: (col('date_mm5310ax')?.text as string) ?? null,
        qbInvoice: (col('text_mm4rzfd4')?.text as string) ?? null,
        dateShipped: (col('date_mm3jza4r')?.text as string) ?? null,
        qbCustomerName: (qbCol?.linked_items?.[0]?.name as string) ?? null,
        colorName: (colorCol?.linked_items?.[0]?.name as string) ?? null,
        poUpload: files,
        rackProgress
      };
    });

    return { jobs };
  });
